import React, { useEffect, useRef, useState } from "react";
import { api, logoUrl, apiError } from "@/lib/api";
import { formatEUR, VAT_RATES, UNITS } from "@/lib/invoice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, User, Package, Plus, Pencil, Trash2, Upload, Loader2, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

const emptySender = {
  label: "Mein Briefkopf", name: "", company: "", address_line1: "", zip: "", city: "",
  country: "Deutschland", phone: "", email: "", website: "", tax_number: "", vat_id: "",
  bank_name: "", iban: "", bic: "", logo_path: "", is_kleinunternehmer: false,
};
const emptyRecipient = {
  name: "", company: "", address_line1: "", zip: "", city: "", country: "Deutschland",
  email: "", customer_number: "",
};
const emptyPosition = { description: "", unit: "Stk.", unit_price: 0, vat_rate: 19 };

export default function Bausteine() {
  const [tab, setTab] = useState("sender");
  return (
    <div className="p-6 md:p-8 max-w-[1100px] mx-auto">
      <h1 className="font-heading text-4xl font-extrabold tracking-tight">Bausteine</h1>
      <p className="text-slate-500 text-sm mt-1 mb-6">
        Vordefinierte Elemente – Briefköpfe, Empfänger und wiederverwendbare Positionen.
      </p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none bg-slate-100 p-0.5 h-auto">
          <TabsTrigger value="sender" data-testid="tab-sender" className="rounded-none gap-2 data-[state=active]:bg-white data-[state=active]:text-primary px-4 py-2">
            <Building2 className="h-4 w-4" /> Briefköpfe
          </TabsTrigger>
          <TabsTrigger value="recipient" data-testid="tab-recipient" className="rounded-none gap-2 data-[state=active]:bg-white data-[state=active]:text-primary px-4 py-2">
            <User className="h-4 w-4" /> Empfänger
          </TabsTrigger>
          <TabsTrigger value="position" data-testid="tab-position" className="rounded-none gap-2 data-[state=active]:bg-white data-[state=active]:text-primary px-4 py-2">
            <Package className="h-4 w-4" /> Positionen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sender" className="mt-6">
          <SenderSection />
        </TabsContent>
        <TabsContent value="recipient" className="mt-6">
          <RecipientSection />
        </TabsContent>
        <TabsContent value="position" className="mt-6">
          <PositionSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function useList(endpoint) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try {
      const res = await api.get(endpoint);
      setItems(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  return { items, loading, load };
}

const Card = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-slate-200 p-4 hover:border-primary hover:bg-slate-50 transition-colors duration-150"
  >
    {children}
  </div>
);

const inputCls = "rounded-none border-slate-300 focus-visible:ring-0 focus-visible:border-primary";

// ---------------------------------------------------------------- SENDER
function SenderSection() {
  const { items, loading, load } = useList("/senders");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySender);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const openNew = () => { setEditing(null); setForm(emptySender); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...emptySender, ...s }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_path", res.data.logo_path);
      toast.success("Logo hochgeladen");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name && !form.company) { toast.error("Name oder Firma erforderlich"); return; }
    setSaving(true);
    try {
      const { id, user_id, created_at, ...payload } = form;
      if (editing) await api.put(`/senders/${editing.id}`, payload);
      else await api.post("/senders", payload);
      toast.success("Briefkopf gespeichert");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (s) => {
    await api.delete(`/senders/${s.id}`);
    toast.success("Gelöscht");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">Dein Absender inkl. Logo, Steuer- & Bankdaten.</p>
        <Button data-testid="add-sender" onClick={openNew} className="rounded-none bg-primary hover:bg-[#00227A] gap-2">
          <Plus className="h-4 w-4" /> Briefkopf
        </Button>
      </div>

      {loading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.length === 0 && <p className="text-slate-400 text-sm">Noch keine Briefköpfe.</p>}
          {items.map((s) => (
            <Card key={s.id} testid={`sender-card-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 border border-slate-200 flex items-center justify-center bg-slate-50 shrink-0 overflow-hidden">
                  {s.logo_path ? (
                    <img src={logoUrl(s.logo_path)} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <ImageIcon className="h-5 w-5 text-slate-300" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate">{s.label}</div>
                  <div className="text-sm text-slate-600 truncate">{s.company || s.name}</div>
                  <div className="text-xs text-slate-400 truncate">
                    {[s.zip, s.city].filter(Boolean).join(" ")}
                    {s.is_kleinunternehmer ? " · §19 UStG" : ""}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-sender-${s.id}`} onClick={() => openEdit(s)} className="text-slate-400 hover:text-primary p-1">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`delete-sender-${s.id}`} onClick={() => del(s)} className="text-slate-400 hover:text-destructive p-1">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Briefkopf bearbeiten" : "Neuer Briefkopf"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <F label="Bezeichnung (intern)">
              <Input data-testid="sender-label" value={form.label} onChange={(e) => set("label", e.target.value)} className={inputCls} />
            </F>

            <div>
              <Label className="text-sm">Logo</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <div className="h-16 w-16 border border-slate-200 flex items-center justify-center bg-slate-50 overflow-hidden">
                  {form.logo_path ? (
                    <img src={logoUrl(form.logo_path)} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-slate-300" />
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="logo-input" />
                <Button type="button" variant="outline" className="rounded-none gap-2" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Logo hochladen
                </Button>
                {form.logo_path && (
                  <button className="text-xs text-slate-400 hover:text-destructive" onClick={() => set("logo_path", "")}>entfernen</button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <F label="Firma"><Input data-testid="sender-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} /></F>
              <F label="Name"><Input data-testid="sender-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></F>
            </div>
            <F label="Straße & Nr."><Input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} className={inputCls} /></F>
            <div className="grid grid-cols-3 gap-3">
              <F label="PLZ"><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} className={inputCls} /></F>
              <F label="Ort" className="col-span-2"><Input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Telefon"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></F>
              <F label="E-Mail"><Input value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></F>
            </div>
            <F label="Webseite"><Input value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Steuernummer"><Input value={form.tax_number} onChange={(e) => set("tax_number", e.target.value)} className={inputCls} /></F>
              <F label="USt-IdNr."><Input value={form.vat_id} onChange={(e) => set("vat_id", e.target.value)} className={inputCls} /></F>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <F label="Bank"><Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} className={inputCls} /></F>
              <F label="IBAN"><Input value={form.iban} onChange={(e) => set("iban", e.target.value)} className={`${inputCls} font-mono`} /></F>
              <F label="BIC"><Input value={form.bic} onChange={(e) => set("bic", e.target.value)} className={`${inputCls} font-mono`} /></F>
            </div>
            <div className="flex items-center justify-between border border-slate-200 p-3">
              <div>
                <div className="text-sm font-medium">Kleinunternehmer (§19 UStG)</div>
                <div className="text-xs text-slate-400">Standardmäßig keine MwSt. ausweisen</div>
              </div>
              <Switch data-testid="sender-klein" checked={form.is_kleinunternehmer} onCheckedChange={(v) => set("is_kleinunternehmer", v)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="save-sender" onClick={save} disabled={saving} className="rounded-none bg-primary hover:bg-[#00227A]">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------------------------------------------- RECIPIENT
function RecipientSection() {
  const { items, loading, load } = useList("/recipients");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRecipient);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(emptyRecipient); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyRecipient, ...r }); setOpen(true); };

  const save = async () => {
    if (!form.name && !form.company) { toast.error("Name oder Firma erforderlich"); return; }
    setSaving(true);
    try {
      const { id, user_id, created_at, ...payload } = form;
      if (editing) await api.put(`/recipients/${editing.id}`, payload);
      else await api.post("/recipients", payload);
      toast.success("Empfänger gespeichert");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const del = async (r) => { await api.delete(`/recipients/${r.id}`); toast.success("Gelöscht"); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">Kunden & Empfänger für schnelle Auswahl.</p>
        <Button data-testid="add-recipient" onClick={openNew} className="rounded-none bg-primary hover:bg-[#00227A] gap-2">
          <Plus className="h-4 w-4" /> Empfänger
        </Button>
      </div>
      {loading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.length === 0 && <p className="text-slate-400 text-sm">Noch keine Empfänger.</p>}
          {items.map((r) => (
            <Card key={r.id} testid={`recipient-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{r.company || r.name}</div>
                  {r.company && r.name && <div className="text-sm text-slate-600 truncate">{r.name}</div>}
                  <div className="text-xs text-slate-400 truncate">{[r.zip, r.city].filter(Boolean).join(" ")}</div>
                  {r.customer_number && <div className="text-xs text-slate-400 font-mono">Kd-Nr. {r.customer_number}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`edit-recipient-${r.id}`} onClick={() => openEdit(r)} className="text-slate-400 hover:text-primary p-1"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-recipient-${r.id}`} onClick={() => del(r)} className="text-slate-400 hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Empfänger bearbeiten" : "Neuer Empfänger"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <F label="Firma"><Input data-testid="recipient-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} /></F>
              <F label="Name"><Input data-testid="recipient-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></F>
            </div>
            <F label="Straße & Nr."><Input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} className={inputCls} /></F>
            <div className="grid grid-cols-3 gap-3">
              <F label="PLZ"><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} className={inputCls} /></F>
              <F label="Ort" className="col-span-2"><Input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="E-Mail"><Input value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></F>
              <F label="Kundennummer"><Input value={form.customer_number} onChange={(e) => set("customer_number", e.target.value)} className={inputCls} /></F>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="save-recipient" onClick={save} disabled={saving} className="rounded-none bg-primary hover:bg-[#00227A]">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------------------------------------------------------------- POSITION
function PositionSection() {
  const { items, loading, load } = useList("/positions");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPosition);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(emptyPosition); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyPosition, ...p }); setOpen(true); };

  const save = async () => {
    if (!form.description) { toast.error("Beschreibung erforderlich"); return; }
    setSaving(true);
    try {
      const payload = {
        description: form.description, unit: form.unit,
        unit_price: Number(form.unit_price) || 0, vat_rate: Number(form.vat_rate) || 0,
      };
      if (editing) await api.put(`/positions/${editing.id}`, payload);
      else await api.post("/positions", payload);
      toast.success("Position gespeichert");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const del = async (p) => { await api.delete(`/positions/${p.id}`); toast.success("Gelöscht"); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">Wiederverwendbare Rechnungspositionen (Leistungen).</p>
        <Button data-testid="add-position" onClick={openNew} className="rounded-none bg-primary hover:bg-[#00227A] gap-2">
          <Plus className="h-4 w-4" /> Position
        </Button>
      </div>
      {loading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.length === 0 && <p className="text-slate-400 text-sm">Noch keine Positionen.</p>}
          {items.map((p) => (
            <Card key={p.id} testid={`position-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">{p.description}</div>
                  <div className="text-sm text-slate-500 font-mono">
                    {formatEUR(p.unit_price)} / {p.unit} · {p.vat_rate}% MwSt.
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`edit-position-${p.id}`} onClick={() => openEdit(p)} className="text-slate-400 hover:text-primary p-1"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-position-${p.id}`} onClick={() => del(p)} className="text-slate-400 hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Position bearbeiten" : "Neue Position"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <F label="Beschreibung"><Input data-testid="position-description" value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></F>
            <div className="grid grid-cols-3 gap-3">
              <F label="Einzelpreis (€)"><Input type="number" data-testid="position-price" value={form.unit_price} onChange={(e) => set("unit_price", e.target.value)} className={`${inputCls} font-mono`} /></F>
              <F label="Einheit">
                <Select value={form.unit} onValueChange={(v) => set("unit", v)}>
                  <SelectTrigger className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="MwSt. %">
                <Select value={String(form.vat_rate)} onValueChange={(v) => set("vat_rate", Number(v))}>
                  <SelectTrigger className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{VAT_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r} %</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="save-position" onClick={save} disabled={saving} className="rounded-none bg-primary hover:bg-[#00227A]">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs text-slate-500">{label}</Label>
    {children}
  </div>
);
